/**
 * UniqueAddFormBody — orchestrator for the `add-unique-constraint` form.
 * Owns state + commit dispatch; renders columns/name + the per-engine extras
 * via `UniqueAddEngineFields` (split per the 200-line file budget).
 *
 * Engine-aware behaviour:
 *   - sqlite: caller (`UniqueAddPopover`) gates rendering with
 *     `isAddUniqueConstraintSupported`; included as a defensive runtime guard.
 *   - mssql: clustered radio enabled. Other engines: clustered absent.
 *   - postgres: INCLUDE input + NULLS DISTINCT radios + DEFERRABLE radios.
 *   - oracle: tablespace input + DEFERRABLE radios.
 *   - mysql: only columns + name.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice B3.
 */
import { useState } from 'react';

import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import {
  ambiguousColumnNameOf,
  isAmbiguousColumnNameError,
} from '../../../../sql-resolver/ddl/apply/columnEdit.alterTable';
import type { UniqueConstraintDef } from '../../../../sql-resolver/types/ddlEdit';
import { parseColumnList } from '../../edit/edits/addPrimaryKey';
import {
  commitAddUniqueConstraint,
  getNullsDistinctEngineVersionNote,
  isClusteredSupported,
  isDeferrableSupported,
  isIncludeColumnsSupported,
  isNullsDistinctSupported,
  isTablespaceSupported,
  validateUniqueConstraintForEngine,
} from '../../edit/edits/addUniqueConstraint';
import type { VqeDdlEditContext } from '../../edit/editContext';
import {
  UniqueAddEngineFields,
  type ClusteredMode,
  type DeferrableMode,
  type NullsMode,
} from './UniqueAddEngineFields';
import { ambiguousColumnMessage } from './ambiguousColumnCopy';
import { FormEngineChip } from './FormEngineChip';

export interface UniqueAddFormBodyProps {
  readonly editContext: VqeDdlEditContext;
  readonly testId: string;
  readonly onCommitted?: () => void;
  readonly onCancel?: () => void;
}

export function UniqueAddFormBody(props: UniqueAddFormBodyProps) {
  const { editContext, testId, onCommitted, onCancel } = props;
  const engine = editContext.service.engineId;
  const showClustered = isClusteredSupported(engine);
  const showInclude = isIncludeColumnsSupported(engine);
  const showNullsDistinct = isNullsDistinctSupported(engine);
  const showTablespace = isTablespaceSupported(engine);
  const showDeferrable = isDeferrableSupported(engine);
  const nullsDistinctNote = getNullsDistinctEngineVersionNote(engine);

  const [columnsInput, setColumnsInput] = useState('');
  const [name, setName] = useState('');
  const [clustered, setClustered] = useState<ClusteredMode>('default');
  const [include, setInclude] = useState('');
  const [nullsMode, setNullsMode] = useState<NullsMode>('unspecified');
  const [tablespace, setTablespace] = useState('');
  const [deferrable, setDeferrable] = useState<DeferrableMode>('unspecified');
  const [error, setError] = useState<string | null>(null);
  /** The thrown error's own text, kept when the box shows a rewritten
   *  sentence — a developer reading the popover still gets the precise one. */
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  function buildUnique(): UniqueConstraintDef | { error: string } {
    const draft: {
      name?: string;
      columns: readonly string[];
      clustered?: 'clustered' | 'nonclustered';
      includeColumns?: readonly string[];
      nullsDistinct?: boolean;
      tablespace?: string;
      deferrable?: 'deferred' | 'immediate';
    } = { columns: parseColumnList(columnsInput) };
    const trimmedName = name.trim();
    if (trimmedName !== '') draft.name = trimmedName;
    if (showClustered && clustered !== 'default') draft.clustered = clustered;
    if (showInclude) {
      const cols = parseColumnList(include);
      if (cols.length > 0) draft.includeColumns = cols;
    }
    if (showNullsDistinct && nullsMode !== 'unspecified') {
      draft.nullsDistinct = nullsMode === 'distinct';
    }
    if (showTablespace) {
      const ts = tablespace.trim();
      if (ts !== '') draft.tablespace = ts;
    }
    if (showDeferrable && deferrable !== 'unspecified') draft.deferrable = deferrable;
    const err = validateUniqueConstraintForEngine(engine, draft);
    if (err !== null) return { error: err };
    return draft;
  }

  async function handleSubmit() {
    setError(null);
    setErrorDetail(null);
    const built = buildUnique();
    if ('error' in built) {
      setError(built.error);
      return;
    }
    setPending(true);
    try {
      await commitAddUniqueConstraint(editContext, { unique: built });
      onCommitted?.();
    } catch (err) {
      const ambiguous = ambiguousColumnNameOf(err);
      if (isAmbiguousColumnNameError(err) && ambiguous !== null) {
        setError(ambiguousColumnMessage(ambiguous, engine, 'type-delimiters'));
        setErrorDetail(err.message);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
      setPending(false);
    }
  }

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold">Add unique constraint</div>
        <FormEngineChip engine={engine} />
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-columns`} className="text-xs">
          Columns <span className="text-destructive">*</span>
        </Label>
        <Input
          id={`${testId}-columns`}
          data-testid={`${testId}-columns`}
          value={columnsInput}
          onChange={(e) => setColumnsInput(e.target.value)}
          placeholder="email"
          className="h-8 font-mono text-xs"
        />
        <span className="text-[10px] text-muted-foreground">Comma-separated column list.</span>
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-name`} className="text-xs">Name (optional)</Label>
        <Input
          id={`${testId}-name`}
          data-testid={`${testId}-name`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="uq_table_email"
          className="h-8 font-mono text-xs"
        />
      </div>

      <UniqueAddEngineFields
        testId={testId}
        showClustered={showClustered}
        showInclude={showInclude}
        showNullsDistinct={showNullsDistinct}
        showTablespace={showTablespace}
        showDeferrable={showDeferrable}
        nullsDistinctNote={nullsDistinctNote}
        clustered={clustered}
        include={include}
        nullsMode={nullsMode}
        tablespace={tablespace}
        deferrable={deferrable}
        setClustered={setClustered}
        setInclude={setInclude}
        setNullsMode={setNullsMode}
        setTablespace={setTablespace}
        setDeferrable={setDeferrable}
      />

      {error ? (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive"
          data-testid={`${testId}-error`}
          title={errorDetail ?? undefined}
        >
          {error}
        </div>
      ) : null}

      <div className="sticky bottom-0 -mx-4 -mb-4 flex items-center justify-end gap-2 border-t bg-popover px-4 py-2">
        <Button type="button" variant="ghost" size="sm" onClick={() => onCancel?.()} data-testid={`${testId}-cancel`}>
          Cancel
        </Button>
        <Button type="button" size="sm" disabled={pending} onClick={() => void handleSubmit()} data-testid={`${testId}-submit`}>
          {pending ? 'Adding…' : 'Add'}
        </Button>
      </div>
    </div>
  );
}
